<template>
  <div class="sf-gallery sf-gallery--custom">
    <div class="sf-gallery__stage">
      <div
        ref="glide"
        class="glide"
      >
        <div
          class="glide__track"
          data-glide-el="track"
        >
          <ul class="glide__slides">
            <li
              v-for="(picture, index) in images"
              :key="'slide-' + index"
              class="glide__slide"
            >
              <span class="counter-image hide-desktop">{{ index + 1 }} / {{ images.length }}</span>
              <span
                v-show="productDiscountPercentage && productDiscountPercentage > 0"
                class="gallery-discount-percentage"
              >
                {{ '-'+productDiscountPercentage+'%' }}
              </span>
              <SfLoader
                :loading="loading"
                :style="
                  loading && {
                    height: `${imageHeight}px`,
                    'max-height': '80vh',
                  }
                "
              >
                <div
                  v-if="
                    picture &&
                      picture.video_content &&
                      picture.video_content.video_url
                  "
                  class="video-wrapper"
                >
                  <client-only>
                    <vimeo-player
                      :ref="'player_' + index"
                      :video-id="picture.video_content.video_url"
                      :options="videoOptions"
                    />
                  </client-only>
                </div>
                <div
                  v-else
                  @mouseover="startZoom(picture)"
                  @mousemove="moveZoom($event, index)"
                  @mouseout="removeZoom(index)"
                  @click="activeZoom(picture)"
                >
                  <SfImage
                    ref="sfGalleryBigImage"
                    class="sf-gallery__big-image"
                    :class="{ 'sf-gallery__big-image--has-zoom': enableZoom }"
                    :src="picture.desktop.url + '?f=a&w=700'"
                    :alt="picture.alt"
                    :width="imageWidth"
                    :height="imageHeight"
                    :loading="index == 0 ? 'eager' : 'lazy'"
                    :max-placeholder-wait="2500"
                    @click="$emit('click:stage', { picture, index })"
                  />
                </div>
              </SfLoader>
            </li>
          </ul>
        </div>
        <div
          class="product-glide__arrows"
          data-glide-el="controls"
        >
          <div
            class="glide__arrow glide__arrow--left"
            data-glide-dir="<"
          />
          <div
            class="glide__arrow glide__arrow--right"
            data-glide-dir=">"
          />
        </div>
      </div>
      <transition name="sf-fade">
        <div
          ref="outSide"
          :class="{
            'display-none':
              !outsideZoom || !pictureSelected || (!outsideZoom && !enableZoom),
          }"
          :style="{ width: `${imageWidth}px`, height: `${imageHeight}px` }"
        >
          <SfImage
            ref="imgZoom"
            class="sf-gallery__zoom"
            :src="pictureSelectedUrl + '?f=a&w=700'"
            :width="imageWidth"
            :height="imageHeight"
            :alt="pictureSelected.alt"
            loading="lazy"
            :max-placeholder-wait="2500"
          />
        </div>
      </transition>
      <transition name="bounce">
        <div
          v-show="isActiveZoom"
          ref="zoomRef"
          class="image-zoom"
          @wheel="isDisableZoom"
          @click="isDisableZoom"
          @mousemove="moveZoom2($event)"
        >
          <SfImage
            ref="imgZoomRef"
            class="sf-gallery__zoom"
            :src="imageZoomUrl"
            :style="{ transform: `translate(${position.x}, -${position.y}%)` }"
            :alt="pictureSelected.alt"
            loading="lazy"
          />
        </div>
      </transition>
    </div>
    <div class="sf-gallery__thumbs">
      <SfLoader :loading="loadingThumbs">
        <div>
          <slot
            name="thumbs"
            v-bind="{ images, active: activeIndex, go }"
          >
            <SfButton
              v-for="(image, index) in images"
              :key="'img-' + index"
              class="sf-button--pure sf-gallery__item"
              :class="{ 'sf-gallery__item--selected': index === activeIndex }"
              :aria-label="'Image ' + index"
              @click="go(index)"
            >
              <img
                class="sf-gallery__thumb"
                :src="image.mobile.url + '?f=a&w=300'"
                :alt="image.alt"
                :width="thumbWidth"
                :height="thumbHeight"
                :loading="isMobile ? 'lazy' : 'eager'"
              >
            </SfButton>
          </slot>
        </div>
      </SfLoader>
    </div>
  </div>
</template>
<script>
// import Embedo from 'embedo';
import Glide from '@glidejs/glide';
import { SfButton, SfLoader } from '@storefront-ui/vue';
import {
  defineComponent,
  ref,
  computed,
  onBeforeUnmount,
} from '@nuxtjs/composition-api';
import {
  mapMobileObserver,
  unMapMobileObserver,
} from '@storefront-ui/vue/src/utilities/mobile-observer.js';
import SfImage from './SfImage';

export default defineComponent({
  name: 'GalleryCustom',
  components: {
    SfImage,
    SfButton,
    SfLoader,
  },
  props: {
    images: {
      type: Array,
      default: () => [],
    },
    imageWidth: {
      type: [Number, String],
      default: 422,
    },
    imageHeight: {
      type: [Number, String],
      default: 664,
    },
    thumbWidth: {
      type: [Number, String],
      default: 160,
    },
    thumbHeight: {
      type: [Number, String],
      default: 160,
    },
    current: {
      type: Number,
      default: 1,
    },
    sliderOptions: {
      type: Object,
      default() {
        return {
          type: 'slider',
          autoplay: false,
          rewind: false,
          gap: 0,
        };
      },
    },
    outsideZoom: {
      type: Boolean,
      default: false,
    },
    enableZoom: {
      type: Boolean,
      default: false,
    },
    productDiscountPercentage: {
      type: [Number, String],
      default: '',
    },
  },
  setup() {
    const loading = ref(true);
    const loadingThumbs = ref(true);
    const isMobile = computed(() => mapMobileObserver().isMobile.get());

    onBeforeUnmount(() => {
      unMapMobileObserver();
    });

    return {
      loading,
      loadingThumbs,
      isMobile,
    };
  },
  data() {
    return {
      positionStatic: {},
      eventHover: {},
      pictureSelected: { alt: '' },
      glide: null,
      activeIndex: this.current - 1,
      style: '',
      imageZoomUrl: '',
      isActiveZoom: false,
      position: {
        x: '-50%',
        y: 0,
      },
      videoOptions: {
        // responsive: true,
        // muted: true,
      },
    };
  },
  computed: {
    // mapPictures() {
    //   // map images to handle picture tags with SfImage
    //   return this.images.map(({ desktop, big }) => ({
    //     mobile: desktop,
    //     desktop: big,
    //   }));
    // },
    updatedSliderOptions() {
      return { ...this.sliderOptions, startAt: 0 };
    },
    pictureSelectedUrl() {
      const { zoom, big, desktop } = this.pictureSelected;
      const definedPicture = zoom || big || desktop;
      return definedPicture ? definedPicture.url : '';
    },
  },
  mounted() {
    const glide = new Glide(this.$refs.glide, this.updatedSliderOptions);
    this.$nextTick(() => {
      // handle slider with swipe and transitions with Glide.js
      // https://glidejs.com/docs/
      if (this.images.length === 0) return;
      glide.on('run', () => {
        this.go(glide.index);
      });

      // eslint-disable-next-line unicorn/no-this-assignment
      const self = this;
      glide.on('move.after', () => {
        if (self.$refs[`player_${glide.index}`]) {
          self.$refs[`player_${glide.index}`][0].play();
        }
      });

      glide.on('run.before', () => {
        if (self.$refs[`player_${glide.index}`]) {
          self.$refs[`player_${glide.index}`][0].pause();
        }
      });

      glide.mount();
      this.glide = glide;
      this.loading = false;
      this.loadingThumbs = false;
    });
    glide.mount();
  },
  updated() {
    if (!this.glide) {
      const glide = new Glide(this.$refs.glide, this.updatedSliderOptions);
      glide.on('run', () => {
        this.go(glide.index);
      });
      glide.mount();
      this.glide = glide;
      this.loading = false;
      this.loadingThumbs = false;
    }
  },
  beforeDestroy() {
    if (this.glide) {
      this.loading = true;
      // this.loadingThumbs = true;
      this.glide.destroy();
    }
  },
  methods: {
    positionObject(index) {
      if (this.$refs.sfGalleryBigImage) {
        if (this.outsideZoom) {
          return this.$refs.glide.getBoundingClientRect();
        }
        return this.$refs.sfGalleryBigImage[index].$el.getBoundingClientRect();
      }
      return '';
    },
    go(index) {
      if (!this.glide) return;
      this.activeIndex = index;
      /**
       * Event for current image change (`v-model`)
       * @type {Event}
       */
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      this.$emit('click', Number.parseInt(index.toString(), 10) + 1);
      if (this.glide) {
        this.glide.go(`=${index}`);
      }
    },
    startZoom(picture) {
      if (this.enableZoom) {
        this.pictureSelected = picture;
      }
    },
    moveZoom($event, index) {
      if (this.enableZoom) {
        this.eventHover = $event;
        if (this.outsideZoom) {
          this.positionStatic = this.positionObject(index);
          this.$refs.imgZoom.$el.children[0].style.cssText = 'top: 0; transform: scale(2);';
          this.$refs.imgZoom.$el.children[0].style.transformOrigin = `${
            $event.clientX - this.positionStatic.x
          }px ${$event.clientY - this.positionStatic.y}px`;
        } else {
          this.positionStatic = this.positionObject(index);
          this.$refs.sfGalleryBigImage[index].$el.children[0].style.cssText = 'top: 0; transform: scale(2);';
          this.$refs.sfGalleryBigImage[
            index
          ].$el.children[0].style.transformOrigin = `${
            $event.clientX - this.positionStatic.x
          }px ${$event.clientY - this.positionStatic.y}px`;
        }
      }
    },
    moveZoom2($event) {
      const percentPosition = (
        ($event.clientY * 100)
        / window.innerHeight
      ).toFixed(0);
      this.position.y = percentPosition;
      if (percentPosition > 65) {
        this.position.y = 65;
      }
    },
    removeZoom(index) {
      if (this.enableZoom) {
        this.pictureSelected = '';
        if (this.outsideZoom) return;
        this.$refs.sfGalleryBigImage[index].$el.children[0].style.transform = 'scale(1)';
      }
    },
    activeZoom(picture) {
      if (window.innerWidth >= 1023) {
        let imageZoomUrl = `${picture?.desktop.url}?f=a`;
        if (picture?.big.url) {
          imageZoomUrl = `${picture?.big.url}?f=a`;
        }
        this.imageZoomUrl = imageZoomUrl;
        this.position.y = 50;
        this.isActiveZoom = true;
      }
    },
    isDisableZoom() {
      this.isActiveZoom = false;
    },
  },
});
</script>
<style lang="scss">
@import '~@storefront-ui/shared/styles/components/molecules/SfGallery.scss';
</style>

<style lang="scss">
.product-glide__bullets {
  display: flex;
  justify-content: center;
  padding: 25px 0;
  @media (min-width: 1024px) {
    display: none;
  }
  .glide__bullet {
    background: #222a24;
    height: 2px;
    width: 20px;
    margin: 0 8px;
    &:not(.glide__bullet--active) {
      opacity: 0.4;
    }
  }
}
.product-glide__arrows {
  display: none;
  @media (max-width: 1023px) {
    display: block;
  }
  .glide__arrow {
    position: absolute;
    top: 50%;
    left: 0;
    transform: translateY(-50%);
    width: 30px;
    height: 40px;
    background-color: #ffffff;
    display: flex;
    justify-content: center;
    align-items: center;
    opacity: 0.9;
    cursor: pointer;
    &::hover {
      opacity: 1;
    }
    &::after {
      content: '';
      display: block;
      border-right: 2px solid;
      border-bottom: 2px solid;
      width: 12px;
      height: 12px;
    }
    &.glide__arrow--left {
      &::after {
        transform: rotate(-225deg);
        margin-left: 4px;
      }
    }
    &.glide__arrow--right {
      left: auto;
      right: 0;
      &::after {
        transform: rotate(-45deg);
        margin-right: 4px;
      }
    }
    &.glide__arrow--disabled {
      display: none;
    }
  }
}
.glide__slide {
  position: relative;
  flex: none !important;
  .gallery-discount-percentage {
    position: absolute;
    z-index: 999;
    top: 0.9375rem;
    left: 0.9375rem;
    color: #053805;
    font-weight: 500;
    font-family: 'Spartan';
    line-height: 1.0625rem;
    font-size: 1.0625rem;
    @media (max-width: 768px) {
      line-height: 0.875rem;
      font-size: 0.875rem;
    }
  }
  .counter-image {
    bottom: 15px;
    color: #053805;
    display: block;
    font-size: 15px;
    font-weight: 500;
    font-family: 'Spartan';
    left: 10px;
    position: absolute;
    width: auto;
    height: 14px;
    z-index: 999;
  }

  .sf-image--wrapper {
    display: block;
    width: 100% !important;
    --image-height: auto !important;
    img:not(.sf-image--placeholder) {
      display: block;
    }
    .sf-image--placeholder {
      padding: 8vh;
    }
  }

  .video-wrapper {
    padding-bottom: 150%;
    position: relative;
    height: 0;
    overflow: hidden;
    iframe {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      display: block;
    }
  }
}

.image-zoom {
  background: #fff;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  inset: 0;
  width: 100%;
  z-index: 999;

  .sf-gallery__zoom {
    cursor: zoom-out;
    left: 50%;
    padding-top: 100px;
    z-index: 999;
    transition: transform 700ms;

    .sf-image:not([alt]),
    .sf-image[alt=''] {
      outline-style: none;
    }
  }
}

.glide--ltr {
  .glide__track {
    height: 100%;
    .glide__slides {
      height: 100%;
      .sf-loader {
        height: 100%;
        @include for-mobile {
          max-height: 140vw;
        }
      }
    }
  }
}

.bounce-enter-active {
  animation: bounce-in 0.5s;
}
.bounce-leave-active {
  animation: bounce-in 0.5s reverse;
}

@keyframes bounce-in {
  0% {
    transform: scale(0.5);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}
</style>

<style lang="scss">
.sf-gallery--custom {
  .glide__slide {
    .sf-image.sf-image-loaded {
      display: block;
      width: 100%;
    }
  }
}
</style>
